/**
 * startAnchor — the day a recurring reminder was told to START, when the user
 * said so explicitly.
 *
 * Walter 2026-09-26 (#245) made the source line keep a start date he typed:
 * "every thu and mon 10/2 10am book squash" → "🔔 every Mon and Fri starting
 * 10/2 at 10am: book squash". That works while the line is the source of
 * truth. But the AGENDA POPOVER rebuilds the line from the stored reminder
 * instead (formatBellLine), and the stored reminder had no idea a start date
 * had ever been typed — so editing the reminder there rewrote his line without
 * the clause. Same silent rewrite, different door (#246).
 *
 * A boolean would not have been enough. `parsedDate` is the NEXT fire, and it
 * moves: the series advances, he snoozes, he edits the time. Once it has moved
 * past the start, "starting <parsedDate>" would name a day that isn't the
 * start at all. So we store the anchor DATE and leave it alone afterwards —
 * the clause then keeps saying when the series began, which is the thing he
 * asked the line to show.
 *
 * Absent on every reminder created before this, which reads as "he didn't type
 * one" — exactly the old behaviour, so nothing needs migrating.
 */

/**
 * startAnchorFor(parse) → Date | null
 *
 * `parse` is a parseReminderLine result. An anchor exists only when the line
 * carries BOTH a recurrence rule and a calendar date he actually typed —
 * `hasCertainDay` is chrono's day-of-MONTH certainty, so "every Wed" (a
 * certain weekday) is not an anchor, while "every Wed 10/2" is.
 */
export function startAnchorFor(parse) {
  if (!parse || !parse.recurrence || !parse.hasCertainDay) return null;
  const d = parse.parsedDate;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return new Date(d.getTime());
}

/** Coerce a stored value (Firestore Timestamp | Date | ISO | millis) to a Date. */
export function toAnchorDate(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v.toDate === 'function') {
    try {
      const d = v.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch { return null; }
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export default startAnchorFor;
